import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, ExternalLink, Search, Loader2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { BackgroundImage } from '../components/BackgroundImage'
import { getFullApiUrl } from '../config/api'

interface Subscription {
  subscriptionId: string
  solutionId: string
  solutionName: string
  planType: string
  status: string
  startDate: string
  endDate?: string
  autoRenew: boolean
  token?: string
  tier?: string
}

export function MySubscriptionsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')

  useEffect(() => {
    fetchSubscriptions()
  }, [user])

  const fetchSubscriptions = async () => {
    try {
      const userEmail = user?.email || localStorage.getItem('userEmail') || localStorage.getItem('email')
      
      if (!userEmail) {
        setError('User email not found. Please login again.')
        setLoading(false)
        return
      }

      const response = await fetch(getFullApiUrl('/api/subscriptions'), {
        headers: { 
          'x-user-email': userEmail
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setSubscriptions(data.subscriptions || [])
      } else if (response.status === 404) {
        setSubscriptions([])
      } else {
        setError('Failed to load subscriptions')
      }
    } catch (err) {
      setSubscriptions([])
    } finally {
      setLoading(false)
    }
  }

  const handleLaunch = (subscription: Subscription) => {
    if (subscription.token && user?.email) {
      const faissBaseUrl = import.meta.env.VITE_FAISS_BASE_URL || 'https://awssolutionfinder.solutions.cloudnestle.com'
      const params = new URLSearchParams({
        token: subscription.token,
        user_email: user.email,
        tier: subscription.tier || 'registered',
        user_id: user.email
      })
      const url = `${faissBaseUrl}/search?${params.toString()}`
      window.open(url, '_blank')
    } else {
      navigate(`/solutions/${subscription.solutionId}`)
    }
  }

  // Filter subscriptions
  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = searchQuery === '' || 
      sub.solutionName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.planType.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesStatus = selectedStatus === 'all' || sub.status === selectedStatus
    
    return matchesSearch && matchesStatus
  })

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedStatus('all')
  }

  const hasActiveFilters = searchQuery !== '' || selectedStatus !== 'all'

  if (loading) {
    return (
      <div className="min-h-screen relative">
        <BackgroundImage />
        <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-16">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading subscriptions...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative">
      <BackgroundImage />
      <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-16">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Subscriptions</h1>
          <p className="text-gray-600">View and manage your active subscriptions</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search subscriptions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear Filters
                </button>
              )}

              <div className="sm:ml-auto flex items-center text-sm text-gray-600">
                Showing <span className="font-semibold mx-1">{filteredSubscriptions.length}</span> of <span className="font-semibold mx-1">{subscriptions.length}</span> subscriptions
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Subscriptions Grid */}
        {subscriptions.length === 0 ? (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8 text-gray-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No subscriptions yet</h2>
              <p className="text-gray-600 mb-6">Browse our catalog to find solutions</p>
              <button
                onClick={() => navigate('/catalog')}
                className="btn-primary"
              >
                Browse Catalog
              </button>
            </div>
          </div>
        ) : filteredSubscriptions.length === 0 ? (
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-gray-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No Subscriptions Found</h2>
              <p className="text-gray-600 mb-6">
                Try adjusting your search or filters to find what you're looking for.
              </p>
              <button
                onClick={clearFilters}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSubscriptions.map((sub) => (
              <div key={sub.subscriptionId} className="bg-white rounded-lg shadow-md border-2 border-gray-200 overflow-hidden hover:shadow-xl hover:border-blue-300 transition-all">
                {/* Solution Name - Clickable */}
                <div className="p-4">
                  <h3 
                    onClick={() => navigate(`/solutions/${sub.solutionId}`)}
                    className="text-lg font-bold text-blue-600 leading-tight cursor-pointer hover:text-blue-700 underline decoration-2 underline-offset-2 transition-colors flex items-center gap-2 mb-3"
                  >
                    {sub.solutionName}
                    <svg className="w-4 h-4 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </h3>

                  {/* Subscription Details */}
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Plan:</span>
                      <span className="font-medium capitalize">{sub.planType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status:</span>
                      <span className={`font-medium ${
                        sub.status === 'active' ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {sub.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Start Date:</span>
                      <span className="font-medium">
                        {new Date(sub.startDate).toLocaleDateString()}
                      </span>
                    </div>
                    {sub.endDate && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">End Date:</span>
                        <span className="font-medium">
                          {new Date(sub.endDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Launch Button */}
                  {sub.status === 'active' && (
                    <button
                      onClick={() => handleLaunch(sub)}
                      className="w-full btn-primary flex items-center justify-center gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Launch Solution
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
